import dayjs, { type Dayjs } from 'dayjs';
import type { UseFormReturn } from 'react-hook-form';

import { Radio, RadioGroup } from '@/components/common/radio';
import { DateRangeField } from '@/components/forms';

import type { DiscountFormValues, TriggerFormValue } from '../../types';

import styles from './discount-create-form.module.scss';

// Figma: plain radio labels (NO inline descriptions) — same pattern as ticket-10 applicability.
const OPTIONS: { value: TriggerFormValue['type']; label: string }[] = [
  { value: 'once_per_customer', label: 'Once Per Customer' },
  { value: 'limited_period', label: 'Limited Period' },
  { value: 'first_time_customer', label: 'First Time Customer' },
];

// Single per-trigger helper line below the radios (the "description" the AC references), swaps by
// selection — asterisk-prefixed, muted, matching Figma 4612-1073166 / -1074498 / -1075830.
const HELP: Record<TriggerFormValue['type'], string> = {
  once_per_customer: '* Limit the discount to a single use per customer.',
  limited_period: '* Make this discount available for a limited time.',
  first_time_customer: '* Available only to customers placing their first order.',
};

// Past dates are disabled in the calendar (today enabled, yesterday-and-before disabled).
const disabledPast = (current: Dayjs) => current && current.isBefore(dayjs().startOf('day'), 'day');

export function TriggerFields({ form }: { form: UseFormReturn<DiscountFormValues> }) {
  const applicability = form.watch('applicability');
  const trigger = form.watch('trigger');

  // First Time Customer is not offered for customer-scoped discounts.
  const options = OPTIONS.filter(
    (o) => !(applicability.type === 'customer' && o.value === 'first_time_customer'),
  );

  // Switch trigger type; preserve the picked window across Once ↔ Limited, drop it for First Time.
  // Both windowed arms accept a null window (the schema refine enforces Limited's requirement), so
  // no cast is needed for the transient pre-pick state.
  const setTriggerType = (type: TriggerFormValue['type']) => {
    const dateRange = trigger && trigger.type !== 'first_time_customer' ? trigger.dateRange : null;
    const next: TriggerFormValue = type === 'first_time_customer' ? { type } : { type, dateRange };
    form.setValue('trigger', next, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <fieldset className={styles.applicability}>
      <legend className={styles.sectionLabel}>
        When can this discount be used? <span className={styles.required}>*</span>
      </legend>

      <RadioGroup
        aria-label="When can this discount be used?"
        className={styles.radios}
        value={trigger?.type}
        onChange={(e) => setTriggerType(e.target.value)}
      >
        {options.map((o) => (
          <Radio key={o.value} value={o.value}>
            {o.label}
          </Radio>
        ))}
      </RadioGroup>

      {/* Cleared after a breaking Step-1 change → prompt re-selection (AC); else the per-trigger helper. */}
      {trigger == null ? (
        <p className={styles.error} role="alert">
          Please select a trigger
        </p>
      ) : (
        <p className={styles.helper}>{HELP[trigger.type]}</p>
      )}

      {trigger != null && trigger.type !== 'first_time_customer' ? (
        <DateRangeField
          name="trigger.dateRange"
          control={form.control}
          label="Date Range"
          required={trigger.type === 'limited_period'}
          disabledDate={disabledPast}
        />
      ) : null}
    </fieldset>
  );
}
